import * as React from 'react'
import { Helmet } from 'react-helmet'
import { inject, observer } from 'mobx-react'
import { Row, Col, Select, Button, Alert, Switch, message } from 'antd'
import { CurrentCompanyStore } from '../../../stores/CurrentCompanyStore'
import { InventoryCostOption, AuditMode, AuditCompleteMode } from '@getgreenline/homi-shared'
import { parseErrorMsg } from '../../../utilities/helpers'
import { Routes } from '../../../constants/Routes'
import { HelpLinks } from '../../../constants/ExternalLinks'

interface Props {
  currentCompanyStore?: CurrentCompanyStore
}

interface State {
  inventoryCostOption: InventoryCostOption
  auditMode: AuditMode
  auditCompleteMode: AuditCompleteMode
  isSaving: boolean
  isExampleShown: boolean
  isBarcodeValidationEnabled: boolean
  isDashboardBarcodeValidationEnabled: boolean
  blockNegativeInventorySale: boolean
  autoRestockRefundedCannabis: boolean
}

@inject('currentCompanyStore')
@observer
export class InventorySettings extends React.Component<Props, State> {
  company = this.props.currentCompanyStore!.company!

  constructor(props: Props) {
    super(props)
    this.state = {
      inventoryCostOption: this.company.inventoryCostOption,
      auditMode: this.company.auditMode,
      auditCompleteMode: this.company.auditCompleteMode,
      isSaving: false,
      isExampleShown: false,
      isBarcodeValidationEnabled: this.company.isBarcodeValidationEnabled,
      isDashboardBarcodeValidationEnabled: this.company.isDashboardBarcodeValidationEnabled,
      blockNegativeInventorySale: this.company.blockNegativeInventorySale,
      autoRestockRefundedCannabis: this.company.autoRestockRefundedCannabis,
    }
  }

  saveInventorySettings = async () => {
    await this.setState({ isSaving: true })

    try {
      await this.props.currentCompanyStore!.update({
        auditMode: this.state.auditMode,
        inventoryCostOption: this.state.inventoryCostOption,
        auditCompleteMode: this.state.auditCompleteMode,
        isBarcodeValidationEnabled: this.state.isBarcodeValidationEnabled,
        isDashboardBarcodeValidationEnabled: this.state.isDashboardBarcodeValidationEnabled,
        blockNegativeInventorySale: this.state.blockNegativeInventorySale,
        autoRestockRefundedCannabis: this.state.autoRestockRefundedCannabis,
      })

      message.success('Inventory settings successfully saved.', 1)
    } catch (error) {
      message.error(
        parseErrorMsg(error) || 'There was an error while saving the inventory settings.',
      )
    } finally {
      this.setState({ isSaving: false })
    }
  }

  toggleIsExampleShown = () => this.setState({ isExampleShown: !this.state.isExampleShown })

  render() {
    const {
      auditMode,
      auditCompleteMode,
      inventoryCostOption,
      isSaving,
      isExampleShown,
      isBarcodeValidationEnabled,
      isDashboardBarcodeValidationEnabled,
      blockNegativeInventorySale,
      autoRestockRefundedCannabis,
    } = this.state

    return (
      <Row className='white-rounded-container'>
        <Helmet>
          <title>Settings | Inventory</title>
          <meta name='page-loaded' content='true' />
        </Helmet>

        <Row className='mb-4'>
          <Col xs={12} lg={6}>
            <div>
              <b>Audit counting mode</b>
            </div>
            <div>
              {auditMode === 'regular' && (
                <span>
                  The regular mode displays the displayed values of the &quot;Expected&quot;,
                  &quot;Diff&quot;, and &quot;Cost&quot; fields in the{' '}
                  <a
                    href={Routes.DASHBOARD_INVENTORY_AUDIT(this.company.id)}
                    target='_blank'
                    rel='noreferrer'
                  >
                    audit page
                  </a>
                  .
                </span>
              )}
              {auditMode === 'blind' && (
                <span>
                  The blind mode promotes more accurate auditing by hiding the &quot;Expected&quot;,
                  &quot;Diff&quot;, and &quot;Cost&quot; fields in the{' '}
                  <a
                    href={Routes.DASHBOARD_INVENTORY_AUDIT(this.company.id)}
                    target='_blank'
                    rel='noreferrer'
                  >
                    audit page
                  </a>
                  .
                </span>
              )}
            </div>
          </Col>
          <Col xs={12} lg={{ offset: 2, span: 4 }}>
            <Select<AuditMode>
              style={{ width: '100%' }}
              value={auditMode}
              onChange={(mode) => this.setState({ auditMode: mode })}
            >
              <Select.Option value='regular'>Regular (discrepancies shown)</Select.Option>
              <Select.Option value='blind'>Blind (discrepancies hidden)</Select.Option>
            </Select>
          </Col>
        </Row>

        <Row className='mb-4'>
          <Col xs={12} lg={6}>
            <div>
              <b>Audit completing mode</b>
            </div>
            <div>
              {auditCompleteMode === 'replace' && (
                <>
                  <p>
                    The replace mode sets inventory to the counted quantity in the audit. It does
                    not take any inventory changes during an audit into account.
                  </p>

                  {isExampleShown ? (
                    <>
                      <label>Example: </label>
                      <p>
                        Albert starts an audit for Purple Kush. Despite its expected quantity of 12,
                        Albert counts Purple Kush to be 10. While the audit is taking place, Calvin,
                        Albert&apos;s co-worker, also manually adds 3 to its inventory.
                      </p>
                      <p>
                        After the audit, the final inventory quantity of Purple Kush is calculated
                        to be <b>13</b> = 10 <small>(current count)</small> + 3{' '}
                        <small>(manual addition)</small>
                      </p>
                    </>
                  ) : (
                    <button
                      onClick={this.toggleIsExampleShown}
                      type='button'
                      className='btn btn-sm btn-outline-info'
                    >
                      Show example
                    </button>
                  )}
                </>
              )}
              {auditCompleteMode === 'relative' && (
                <>
                  <p>
                    The relative mode accounts for any inventory changes including sales/refunds
                    since the start of the audit or last expected refresh date.
                  </p>

                  {isExampleShown ? (
                    <>
                      <Alert
                        type='info'
                        message='Example'
                        description={
                          <>
                            <p>
                              Albert starts an audit for Purple Kush. Despite its expected quantity
                              of 12, Albert counts Purple Kush to be 10. The inventory difference is
                              -2 = 10 <small>(current count)</small> - 12{' '}
                              <small>(expected quantity)</small>.
                            </p>
                            <p>
                              While the audit is taking place, 3 Purple Kushes are sold. Therefore,
                              the current inventory quantity is 9 = 12{' '}
                              <small>(expected quantity)</small> - 3{' '}
                              <small>(sales during audit)</small>. Calvin, Albert&apos;s co-worker,
                              also manually adds 1 to its inventory.{' '}
                            </p>
                            <p>
                              After the audit is complete, the final inventory quantity of Purple
                              Kush is calculated to be <b>8</b> = -2{' '}
                              <small>(inventory difference)</small> + 9{' '}
                              <small>(current inventory quantity)</small> + 1{' '}
                              <small>(manual addition)</small>
                            </p>
                          </>
                        }
                      />
                      <br />
                      <Button onClick={this.toggleIsExampleShown}>Hide example</Button>
                    </>
                  ) : (
                    <Button onClick={this.toggleIsExampleShown}>Show example</Button>
                  )}
                </>
              )}
            </div>
          </Col>
          <Col xs={12} lg={{ offset: 2, span: 4 }}>
            <Select<AuditCompleteMode>
              style={{ width: '100%' }}
              value={auditCompleteMode}
              onChange={(mode) => this.setState({ auditCompleteMode: mode })}
            >
              <Select.Option value='replace'>Replace</Select.Option>
              <Select.Option value='relative'>Relative (default)</Select.Option>
            </Select>
          </Col>
        </Row>

        <Row className='mb-4'>
          <Col xs={12} lg={6}>
            <div>
              <b>Inventory cost calculation</b>
            </div>
            <div>
              {inventoryCostOption === 'lastCost' && (
                <p>The cost of your product is set by your latest purchase order cost.</p>
              )}

              {inventoryCostOption === 'weightedAverage' && (
                <p>
                  Average cost = (value of current item inventory + value of new received inventory)
                  / Total item inventory quantity after receiving
                </p>
              )}
            </div>
          </Col>
          <Col xs={12} lg={{ offset: 2, span: 4 }}>
            <Select<InventoryCostOption>
              style={{ width: '100%' }}
              value={inventoryCostOption}
              onChange={(mode) => this.setState({ inventoryCostOption: mode })}
            >
              <Select.Option value='lastCost'>Last cost (default)</Select.Option>
              <Select.Option value='weightedAverage'>Weighted average</Select.Option>
            </Select>
          </Col>
        </Row>

        <Row className='mb-3'>
          <Col xs={12} lg={6}>
            <div>
              <b>Parked sale barcode validation</b>
            </div>
            <div>
              This forces employees to validate pre-built parked sales with barcode scanners.
            </div>
          </Col>
          <Col xs={12} lg={{ offset: 2, span: 4 }}></Col>
        </Row>
        <Row className='mb-3'>
          <Col xs={12} lg={6}>
            <div>
              Validate on Dashboard (under the Parked Sales page)
              <br />
              Learn more{' '}
              <Button
                href={HelpLinks.DASHBOARD_BARCODE_VALIDATION}
                type='link'
                target='_blank'
                style={{ padding: 0 }}
              >
                here.
              </Button>
            </div>
          </Col>
          <Col xs={12} lg={{ offset: 2, span: 4 }}>
            <Switch
              checked={isDashboardBarcodeValidationEnabled}
              onChange={() =>
                this.setState({
                  isDashboardBarcodeValidationEnabled: !isDashboardBarcodeValidationEnabled,
                })
              }
            />
          </Col>
        </Row>
        <Row className='mb-4'>
          <Col xs={12} lg={6}>
            <div>
              Validate on POS (when a parked sale is pulled up)
              <br />
              Learn more{' '}
              <Button
                href={HelpLinks.POS_BARCODE_VALIDATION}
                type='link'
                target='_blank'
                style={{ padding: 0 }}
              >
                here.
              </Button>
            </div>
          </Col>
          <Col xs={12} lg={{ offset: 2, span: 4 }}>
            <Switch
              checked={isBarcodeValidationEnabled}
              onChange={() =>
                this.setState({ isBarcodeValidationEnabled: !isBarcodeValidationEnabled })
              }
            />
          </Col>
        </Row>
        <Row className='mb-4'>
          <Col xs={12} lg={6}>
            <div>
              <b>Block negative inventory sales</b>
              <br />
              Requires manager override to allow a negative inventory sale
            </div>
          </Col>
          <Col xs={12} lg={{ offset: 2, span: 4 }}>
            <Switch
              checked={blockNegativeInventorySale}
              onChange={() =>
                this.setState({ blockNegativeInventorySale: !blockNegativeInventorySale })
              }
            />
          </Col>
        </Row>
        <Row className='mb-4'>
          <Col xs={12} lg={6}>
            <div>
              <b>Automatic restock for refunded cannabis products</b>
              <br />
              Non-cannabis products have optional restocking
            </div>
          </Col>
          <Col xs={12} lg={{ offset: 2, span: 4 }}>
            <Switch
              checked={autoRestockRefundedCannabis}
              onChange={() =>
                this.setState({ autoRestockRefundedCannabis: !autoRestockRefundedCannabis })
              }
            />
          </Col>
        </Row>
        <Row className='mb-4'></Row>

        <Button type='primary' loading={isSaving} onClick={() => this.saveInventorySettings()}>
          Save
        </Button>
      </Row>
    )
  }
}
